// Apps & menus: status + update rules per platform, then the app menu switches
// (owner: "combine app menu and overview", 2026-09-29).
import React, { useCallback, useEffect, useState } from 'react';
import type { MobileAppId, MobilePlatform, ReleaseEnv, MobileUpdateRules, MobileWebDeploy } from '@platform/shared-types';
import { RubyLoader } from '../../components/RubyLoader';
import { mobileApi } from './mobileApi';
import type { MobileStats, NativeInfo } from './mobileApi';
import { Card, Chip, Seg, Btn, PLATFORMS } from './ui';
import { AppMenuEditor } from './AppMenuEditor';

type Rules = Omit<MobileUpdateRules, 'updatedAt' | 'updatedBy'>;
const EMPTY: Rules = { requiredNativeBuild: 0, recommendedNativeBuild: 0, applyMode: 'next-open' };

function PlatformPanel({ app, env, platform, native, notify }: {
  app: MobileAppId; env: ReleaseEnv; platform: MobilePlatform; native: NativeInfo | null; notify: (t: 'success' | 'error', m: string) => void;
}) {
  const [rules, setRules] = useState<Rules | null>(null);
  const [deploy, setDeploy] = useState<MobileWebDeploy | null>(null);
  const [stats, setStats] = useState<MobileStats | null>(null);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState('');

  useEffect(() => {
    let off = false;
    setRules(null); setLoadError('');
    Promise.all([
      // A FAILED read must never become "no rules" — saving defaults over real
      // rules would unblock phones that must stay blocked (review I6).
      mobileApi.rules(app, env, platform).then((r) => r.rules ?? EMPTY).catch((e: Error) => { if (!off) setLoadError(e.message || 'error'); return null; }),
      mobileApi.deploy(app, env, platform).then((r) => r.deploy).catch(() => null),
      mobileApi.stats(app, env, platform).catch(() => null),
    ]).then(([r, d, s]) => { if (!off && r) { setRules({ requiredNativeBuild: r.requiredNativeBuild, recommendedNativeBuild: r.recommendedNativeBuild, applyMode: r.applyMode, storeUrl: r.storeUrl }); setDeploy(d); setStats(s); } });
    return () => { off = true; };
  }, [app, env, platform]);

  const label = platform === 'ios' ? 'iPhone' : 'Android';
  const num = (v: string) => Math.max(0, Math.floor(Number(v) || 0));
  const invalid = !!rules && rules.recommendedNativeBuild < rules.requiredNativeBuild;

  return (
    <Card title={label} right={deploy ? <Chip tone={deploy.status === 'active' ? 'info' : deploy.status === 'complete' ? 'ok' : deploy.status === 'paused' ? 'warn' : 'bad'}>{deploy.bundleId} · {deploy.status === 'complete' ? 'Everyone' : `${deploy.percent}%`}</Chip> : <Chip>No web update yet</Chip>}>
      {loadError ? <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">Couldn't load the update rules ({loadError}). Reload before changing them.</p>
        : !rules ? <div className="relative h-40"><RubyLoader variant="section" /></div> : (
        <div className="flex flex-col gap-4">
          <dl className="grid grid-cols-2 gap-y-1 text-sm">
            <dt className="text-stone-500">Store build</dt><dd className="text-right font-medium">{native ? `${native.nativeVersion} (${native.nativeBuild})` : '—'}</dd>
            <dt className="text-stone-500">Phones (24 h)</dt><dd className="text-right font-medium">{stats?.phones ?? '—'}</dd>
          </dl>
          {stats && stats.phones > 0 && (
            <div className="flex flex-wrap gap-1.5">
              <Chip tone="ok">Notifications {stats.permissions.notifications}%</Chip>
              <Chip tone="ok">Camera {stats.permissions.camera}%</Chip>
              <Chip tone={stats.permissions.biometrics >= 50 ? 'ok' : 'warn'}>Face ID {stats.permissions.biometrics}%</Chip>
            </div>
          )}
          <div className="grid gap-3 border-t border-stone-100 pt-4 text-sm">
            <div className="flex items-center justify-between gap-3"><span>Web updates</span>
              <Seg value={rules.applyMode} onChange={(v) => setRules({ ...rules, applyMode: v })} options={[{ id: 'instant', label: 'Instant' }, { id: 'next-open', label: 'Next open' }]} />
            </div>
            <label className="flex items-center justify-between gap-3"><span>Required build<span className="block text-xs text-stone-500">Older phones are blocked</span></span>
              <input inputMode="numeric" className="h-11 w-24 rounded-xl border border-stone-200 px-3 text-right font-mono" value={rules.requiredNativeBuild} onChange={(e) => setRules({ ...rules, requiredNativeBuild: num(e.target.value) })} aria-label={`${label} required build`} />
            </label>
            <label className="flex items-center justify-between gap-3"><span>Recommended build<span className="block text-xs text-stone-500">Asks, can skip</span></span>
              <input inputMode="numeric" className="h-11 w-24 rounded-xl border border-stone-200 px-3 text-right font-mono" value={rules.recommendedNativeBuild} onChange={(e) => setRules({ ...rules, recommendedNativeBuild: num(e.target.value) })} aria-label={`${label} recommended build`} />
            </label>
            <label className="grid gap-1"><span>Store link</span>
              <input className="h-11 rounded-xl border border-stone-200 px-3" placeholder={platform === 'ios' ? 'https://apps.apple.com/app/id…' : 'https://play.google.com/store/apps/details?id=…'}
                value={rules.storeUrl ?? ''} onChange={(e) => setRules({ ...rules, storeUrl: e.target.value || undefined })} aria-label={`${label} store link`} />
            </label>
            {invalid && <p className="text-xs text-red-700">Recommended must be at or above required.</p>}
            <div className="flex justify-end">
              <Btn primary disabled={saving || invalid} onClick={async () => {
                setSaving(true);
                try { await mobileApi.saveRules(app, env, platform, rules); notify('success', `${label} rules saved`); }
                catch (e: any) { notify('error', e.message); } finally { setSaving(false); }
              }}>Save {label} rules</Btn>
            </div>
          </div>
        </div>
      )}
    </Card>
  );
}

export function AppsTab({ app, env, notify }: { app: MobileAppId; env: ReleaseEnv; notify: (t: 'success' | 'error', m: string) => void }) {
  const [native, setNative] = useState<Record<MobilePlatform, NativeInfo | null> | null>(null);
  const load = useCallback(() => { mobileApi.builds(app, env).then((r) => setNative(r.native)).catch(() => setNative({ ios: null, android: null })); }, [app, env]);
  useEffect(load, [load]);
  return (
    <div className="flex flex-col gap-5">
      <div className="grid gap-5 lg:grid-cols-2">
        {PLATFORMS.map((p) => <PlatformPanel key={`${app}-${p.id}`} app={app} env={env} platform={p.id} native={native?.[p.id] ?? null} notify={notify} />)}
      </div>
      <AppMenuEditor app={app} env={env} notify={notify} />
    </div>
  );
}
